import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShareNetwork, ArrowSquareOut, Copy, ShieldCheck } from '@phosphor-icons/react';
import axiosInstance from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { withUtm } from '../../utils/analytics';

/**
 * MyPublicProfileBanner — shown on contractor/worker dashboards
 * Provisions the slug lazily, displays the public profile URL,
 * and lets the user copy/open/share it.
 */
export default function MyPublicProfileBanner({ userType = 'contractor' }) {
  const [slug, setSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axiosInstance.get('/profiles/me/slug');
        if (!cancelled && resp.data?.slug) setSlug(resp.data.slug);
      } catch {
        // silent fail — banner just won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !slug) return null;

  const path = `/pro/${slug}`;
  const fullUrl = `${window.location.origin}${path}`;
  // OG-tagged share URL — auto-redirects humans to /pro/{slug} but exposes
  // rich-preview meta tags for crawlers (iMessage, Slack, LinkedIn, X, FB).
  const shareUrl = `${window.location.origin}/api/profiles/og/${slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(withUtm(shareUrl, { source: 'pro_share', medium: 'copy_link', campaign: 'profile_share' }));
      toast({ title: 'Link copied', description: 'Paste it anywhere — chats, socials, business cards. Recipients see a rich preview.' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'My ConstructHelp profile', url: withUtm(shareUrl, { source: 'pro_share', medium: 'webshare', campaign: 'profile_share' }) }); return; } catch {}
    }
    copy();
  };

  return (
    <div
      className="mb-6 bg-gradient-to-br from-[#2563EB]/30 to-[#2563EB]/10 dark:from-[#2B4C6F]/20 dark:to-[#2B4C6F]/10 border-2 border-[#0F172A] rounded-xl p-4 flex items-center gap-4 shadow-[3px_3px_0px_#2563EB]"
      data-testid="my-public-profile-banner"
    >
      <div className="hidden sm:flex w-12 h-12 bg-[#2563EB] rounded-lg border-2 border-[#0F172A] items-center justify-center flex-shrink-0">
        <ShieldCheck size={24} weight="fill" className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-[#64748B]">Your Public Profile is Live</p>
        <p className="text-sm font-mono text-neutral-700 dark:text-neutral-300 truncate" data-testid="public-profile-url">
          {fullUrl}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to={path}
          className="hidden md:flex items-center gap-1 h-9 px-3 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-white text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
          data-testid="view-public-profile-btn"
        >
          <ArrowSquareOut size={14} weight="bold" /> View
        </Link>
        <button
          onClick={copy}
          className="flex items-center gap-1 h-9 px-3 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-white text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
          data-testid="copy-public-profile-btn"
        >
          <Copy size={14} weight="bold" /> Copy
        </button>
        <button
          onClick={share}
          className="flex items-center gap-1 h-9 px-3 bg-[#3B82F6] text-[#0F172A] text-xs font-black uppercase tracking-wider rounded-lg border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] hover:shadow-[3px_3px_0px_#0F172A] active:translate-y-0.5 active:shadow-none transition-all"
          data-testid="share-public-profile-btn"
        >
          <ShareNetwork size={14} weight="bold" /> Share
        </button>
      </div>
    </div>
  );
}
